import { KeyRound, LogOut } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAuth } from '../features/auth/AuthProvider';
import { ChangePasswordDialog } from './ChangePasswordDialog';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? '?';
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function UserMenu() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    itemRef.current?.focus();
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!session) return null;
  const { user, tenant } = session;

  const signOut = async () => {
    setBusy(true);
    try {
      await logout();
    } finally {
      void navigate('/login', { replace: true });
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Conta: ${user.display_name}`}
        onClick={() => {
          setOpen((o) => !o);
        }}
        className="flex h-8 items-center gap-2 rounded-md px-1.5 hover:bg-surface-hover"
      >
        <span
          aria-hidden="true"
          className="grid size-6 place-items-center rounded-full bg-accent-soft text-2xs font-semibold text-accent"
        >
          {initials(user.display_name)}
        </span>
        <span className="hidden max-w-36 truncate text-[13px] text-fg-muted lg:block">
          {tenant.name}
        </span>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Conta"
          className="absolute top-full right-0 z-40 mt-1.5 w-64 overflow-hidden rounded-lg border border-line-strong bg-surface-raised shadow-2xl shadow-black/50"
        >
          <div className="border-b border-line px-3.5 py-3">
            <div className="truncate text-[13px] font-medium text-fg">{user.display_name}</div>
            <div className="truncate text-xs text-fg-muted">{user.email}</div>
            <div className="mt-2 inline-flex max-w-full items-center gap-1.5 rounded bg-surface-hover px-1.5 py-0.5 text-2xs text-fg-muted">
              <span className="text-fg-subtle">Organização</span>
              <span className="truncate font-mono">{tenant.slug}</span>
            </div>
          </div>
          <div className="p-1">
            <button
              ref={itemRef}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setChangingPassword(true);
              }}
              className="flex h-8 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] text-fg-muted hover:bg-surface-hover hover:text-fg focus-visible:outline-offset-[-2px]"
            >
              <KeyRound aria-hidden="true" className="size-4" strokeWidth={1.75} />
              Alterar senha
            </button>
            <button
              type="button"
              role="menuitem"
              disabled={busy}
              onClick={() => {
                void signOut();
              }}
              className="flex h-8 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] text-fg-muted hover:bg-surface-hover hover:text-fg focus-visible:outline-offset-[-2px] disabled:opacity-50"
            >
              <LogOut aria-hidden="true" className="size-4" strokeWidth={1.75} />
              {busy ? 'Saindo…' : 'Sair'}
            </button>
          </div>
        </div>
      )}
      {changingPassword && (
        <ChangePasswordDialog
          onClose={() => {
            setChangingPassword(false);
          }}
        />
      )}
    </div>
  );
}
